"use client";

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { getPermission } from './action';

export interface Tab {
  id: number;
  order: number;
  label: string;
  link?: string;
  matchPattern?: RegExp;
}

// SVG Arrow components
const ChevronLeft = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const ChevronRight = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 18l6-6-6-6" />
  </svg>
);

interface ScrollableTabsProps {
  tabs?: Tab[];
  actionTabId?: number;
  onTabChange?: (tabId: number) => void;
  scrollAmount?: number;
}

const ScrollableTabs = ({
  onTabChange,
  scrollAmount = 200
}: ScrollableTabsProps) => {
  const pathname = usePathname();
  const router = useRouter();
  const projectCode = pathname.match(/\/project\/([^/]+)/)?.[1];
  // const resourceGuid = pathname.match(/\/resource\/([^/]+)/)?.[1];
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const tabsRef = useRef<HTMLDivElement | null>(null);
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [activeTabId, setActiveTabId] = useState<number | null>(null);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (projectCode) {
        const perms = await getPermission(projectCode);

        setTabs([
          { id: 1, order: 1, label: 'Documents', link: `/project/${projectCode}/navigation`, matchPattern: /\/project\/[^/]+\/[navigation|resource]/ },
          ...(perms.elementType ? [{ id: 2, order: 2, label: 'Dashboard', link: `/project/${projectCode}/bim-data/dashboard`, matchPattern: /\/project\/[^/]+\/bim-data\/dashboard/ }] : []),
          ...(perms.elementType ? [{ id: 3, order: 3, label: 'Classification', link: `/project/${projectCode}/bim-data/element-type`, matchPattern: /\/project\/[^/]+\/bim-data\/element-type/ }] : []),
          ...(perms.bimModel ? [{ id: 5, order: 5, label: 'LOD', link: `/project/${projectCode}/bim-data/bim-model-status`, matchPattern: /\/project\/[^/]+\/bim-data\/bim-model-status/ }] : []),
          ...(perms.designSpecification ? [{ id: 6, order: 6, label: 'Specification', link: `/project/${projectCode}/bim-data/design-specification`, matchPattern: /\/project\/[^/]+\/bim-data\/design-specification/ }] : []),
          ...(perms.designValidation ? [{ id: 7, order: 7, label: 'Validation', link: `/project/${projectCode}/bim-data/design-validation`, matchPattern: /\/project\/[^/]+\/bim-data\/design-validation/ }] : []),
          ...(perms.costSpecification ? [{ id: 8, order: 8, label: 'Procurement', link: `/project/${projectCode}/bim-data/procurement-estimation`, matchPattern: /\/project\/[^/]+\/bim-data\/procurement-estimation/ }] : []),
          ...(perms.costValidation ? [{ id: 9, order: 9, label: 'Procurement Validation', link: `/project/${projectCode}/bim-data/procurement-validation`, matchPattern: /\/project\/[^/]+\/bim-data\/procurement-validation/ }] : []),
        ]);
      }
    };
    fetchPermissions();
  }, [projectCode]);

  useEffect(() => {
    const activeTab = tabs.find(item => item.matchPattern?.test(pathname));
    setActiveTabId(activeTab?.id ?? null);
  }, [tabs, pathname]);

  const handleTabChange = useCallback((tabId: number) => {
    onTabChange?.(tabId);

    if (tabs) {
      const link = tabs.find(tab => tab.id === tabId)?.link;
      if (link) {
        router.push(link);
      }
    }
  }, [router, tabs, onTabChange]);

  const checkScroll = useCallback(() => {
    if (tabsRef.current) {
      const { scrollWidth, clientWidth } = tabsRef.current;
      setShowLeftArrow(scrollWidth !== clientWidth);
      setShowRightArrow(scrollWidth !== clientWidth);
    }
  }, [tabs]);

  const scroll = useCallback((direction: 'left' | 'right') => {
    if (tabsRef.current) {
      const container = tabsRef.current;
      const currentScroll = container.scrollLeft;
      const targetScroll = direction === 'left'
        ? currentScroll - scrollAmount
        : currentScroll + scrollAmount;

      container.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });

      // Fallback for Safari
      const checkScrollComplete = () => {
        if (container.scrollLeft === currentScroll) {
          container.scrollLeft = targetScroll;
        }
      };
      setTimeout(checkScrollComplete, 200);
    }
  }, [scrollAmount]);

  useEffect(() => {
    const tabsContainer = tabsRef.current;
    if (tabsContainer) {
      checkScroll();
      tabsContainer.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);

      return () => {
        tabsContainer.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
  }, [checkScroll]);

  if (!tabs || tabs.length === 0) {
    return (
      <>
      </>
    );
  }

  if (!projectCode) {
    return (
      <>
      </>
    );
  }

  return (
    <>
      <div className="flex-1 min-w-0 mx-8 select-none">
        {tabs.length > 1 && (
          <div className="relative flex items-center bg-white dark:bg-gray-800 dark:text-gray-200 rounded-lg">
            {showLeftArrow && (
              <button
                onClick={() => scroll('left')}
                className="p-2 rounded-l-lg shrink-0 text-gray-600"
                aria-label="Scroll left"
                type="button"
              >
                <ChevronLeft />
              </button>
            )}

            <div
              ref={tabsRef}
              className="flex-1 overflow-x-auto scrollbar-hide"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch',
                touchAction: 'pan-x',
                position: 'relative'
              }}
            >
              <div className="flex space-x-1 p-1 min-w-max justify-center">
                {tabs.sort((a, b) => a.order - b.order).map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => activeTabId === tab.id ? null : handleTabChange(tab.id)}
                    className={`px-4 py-1 xl:py-2 rounded-md whitespace-nowrap transition-all duration-200 font-semibold ${activeTabId === tab.id
                      ? 'bg-blue-500 text-white dark:text-gray-200'
                      : ' text-gray-700 dark:text-gray-400'
                      }`}
                    type="button"
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {showRightArrow && (
              <button
                onClick={() => scroll('right')}
                className="p-2 rounded-r-lg shrink-0 text-gray-600"
                aria-label="Scroll right"
                type="button"
              >
                <ChevronRight />
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default ScrollableTabs;